import Phaser from 'phaser';

export default class StartScene extends Phaser.Scene {
  private lastScore: number = 0;
  private highScore: number = 0;
  private splashSound!: Phaser.Sound.BaseSound;
  private introVoices: Phaser.Sound.BaseSound[] = [];
  private readyVoices: Phaser.Sound.BaseSound[] = [];

  constructor() {
    super({ key: 'StartScene' });
  }

  preload(): void {
    // Preload assets needed for the start screen
    this.load.image('logo', '/assets/ddd-logo.png');
    this.load.image('diver-transparent', '/assets/diver-transparent.png');
    
    // Preload sounds
    this.load.audio('jump-into-the-water', '/assets/jump-into-the-water.wav');
    
    // Preload voice samples
    // Intro voices
    this.load.audio('intro-hey-im-dave', '/assets/hey-im-dave-but-most-call-me-deep-diver-dave.mp3');
    
    // Ready to play voices
    this.load.audio('lets-dive-in', '/assets/lets-dive-in.mp3');
    this.load.audio('ocean-awaits', '/assets/ocean-awaits.mp3');
    this.load.audio('ready-to-set-record', '/assets/ready-to-set-a-new-record.mp3');
    this.load.audio('lets-hit-the-abyss', '/assets/lets-hit-the-abyss.mp3');
    this.load.audio('lets-hit-bubbles', '/assets/lets-hit-those-bubbles.mp3');
    this.load.audio('hey-lets-go', '/assets/hey-lets-go.mp3');
    this.load.audio('going-down', '/assets/going-down.mp3');
  }

  init(data: { score?: number }): void {
    // If we have a score from the previous game, store it
    if (data.score !== undefined) {
      this.lastScore = data.score;
      
      // Update high score if needed
      const savedHighScore = localStorage.getItem('deepDiveHighScore');
      this.highScore = savedHighScore ? parseInt(savedHighScore, 10) : 0;
      
      if (this.lastScore > this.highScore) {
        this.highScore = this.lastScore;
        localStorage.setItem('deepDiveHighScore', this.highScore.toString());
      }
    }
  }

  create(): void {
    const { width, height } = this.scale;
    
    // Create background
    this.createBackground();
    
    // Set up voice samples
    this.setupVoiceSamples();
    
    // Play splash sound when the start screen loads
    this.splashSound = this.sound.add('jump-into-the-water', { volume: 0.5 });
    this.splashSound.play();
    
    // Play an intro voice line with a delay
    this.time.delayedCall(500, () => {
      // Check if it's the first time or returning after game over
      if (!localStorage.getItem('hasPlayedIntro')) {
        // First time - play full intro
        const introVoice = Phaser.Math.RND.pick(this.introVoices);
        introVoice.play();
        localStorage.setItem('hasPlayedIntro', 'true');
      } else {
        // Returning player - play a random ready voice
        const randomReadyVoice = Phaser.Math.RND.pick(this.readyVoices);
        randomReadyVoice.play({ volume: 0.7 });
      }
    });
    
    // Add logo instead of text title
    const logo = this.add.image(width / 2, height / 6, 'logo');
    // Scale the logo to fit nicely at the top
    logo.setScale(0.4);
    logo.x -= 10;
    logo.setDepth(102);
    
    // Add diver image underneath the logo
    const diver = this.add.image(width / 2, height / 1.5, 'diver-transparent');
    // Scale the diver to fill the remaining space
    const diverScale = (height * 0.9) / diver.height;
    diver.setScale(diverScale);
    diver.x += 40;
    
    // Add start button with fully rounded corners
    const buttonWidth = 200;
    const buttonHeight = 60;
    const cornerRadius = buttonHeight / 2; // Fully rounded corners
    
    const startButton = this.add.graphics();

    startButton.fillStyle(0x20609a, 1);
    startButton.fillRoundedRect(width / 2 - buttonWidth / 2, height / 1.4 - buttonHeight / 2, buttonWidth, buttonHeight, cornerRadius);
    startButton.setInteractive(new Phaser.Geom.Rectangle(width / 2 - buttonWidth / 2, height / 1.4 - buttonHeight / 2, buttonWidth, buttonHeight), Phaser.Geom.Rectangle.Contains);
    startButton.setDepth(100);

    const startText = this.add.text(width / 2, height / 1.4, 'START', {
      fontSize: '24px',
      fontFamily: 'Arial',
      color: '#ffffff',
      fontStyle: 'bold'
    });
    startText.setOrigin(0.5);
    startText.setDepth(101);
    
    // Button hover effect
    startButton.on('pointerover', () => {
      startButton.clear();
      startButton.fillStyle(0x0088cc, 1);
      startButton.fillRoundedRect(width / 2 - buttonWidth / 2, height / 1.4 - buttonHeight / 2, buttonWidth, buttonHeight, cornerRadius);
    });
    
    startButton.on('pointerout', () => {
      startButton.clear();
      startButton.fillStyle(0x00aaff, 1);
      startButton.fillRoundedRect(width / 2 - buttonWidth / 2, height / 1.4 - buttonHeight / 2, buttonWidth, buttonHeight, cornerRadius);
    });
    
    // Start game on button click
    startButton.on('pointerdown', () => {
      // Play a random dive voice
      const randomReadyVoice = Phaser.Math.RND.pick(this.readyVoices);
      randomReadyVoice.play({ volume: 0.7 });
      
      // Delay scene transition to allow voice to play
      this.cameras.main.fade(500, 0, 0, 0);
      this.time.delayedCall(500, () => {
        this.scene.start('GameScene');
      });
    });
    
    // Display scores if we have them
    if (this.lastScore > 0) {
      this.add.text(width / 2, height / 2 + 60, `Last Dive: ${this.lastScore}m`, {
        fontSize: '18px',
        fontFamily: 'Arial',
        color: '#ffffff',
        backgroundColor: '#00223F',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0.5);
    }
    
    if (this.highScore > 0) {
      this.add.text(width / 2, height / 2 + 85, `Deepest Dive: ${this.highScore}m`, {
        fontSize: '18px',
        fontFamily: 'Arial',
        color: '#ffffff',
        stroke: '#000000',
        backgroundColor: '#00223F',
        strokeThickness: 2
      }).setOrigin(0.5);
    }
    
    // Add game instructions with smaller font
    const instructions = [
      'Tap/click to make Dave move',
      'Collect bubbles to gain oxygen',
      'Longer movements use more oxygen',
      'Dive as deep as possible!'
    ];
    
    instructions.forEach((text, index) => {
      this.add.text(width / 2, height / 1.4 + 70 + (index * 18), text, {
        fontSize: '14px',
        fontFamily: 'Arial',
        color: '#ffffff',
        stroke: '#000000',
        backgroundColor: '#00223F',
        strokeThickness: 1
      }).setOrigin(0.5);
    });
    
    // Add bubbles animation for visual effect
    this.createBubbleEffects();
  }
  
  private setupVoiceSamples(): void {
    // Set up intro voice samples
    this.introVoices = [
      this.sound.add('intro-hey-im-dave', { volume: 0.7 })
    ];
    
    // Set up ready to play voice samples
    this.readyVoices = [
      this.sound.add('lets-dive-in', { volume: 0.7 }),
      this.sound.add('ocean-awaits', { volume: 0.7 }),
      this.sound.add('ready-to-set-record', { volume: 0.7 }),
      this.sound.add('lets-hit-the-abyss', { volume: 0.7 }),
      this.sound.add('lets-hit-bubbles', { volume: 0.7 }),
      this.sound.add('hey-lets-go', { volume: 0.7 }),
      this.sound.add('going-down', { volume: 0.7 })
    ];
  }
  
  private createBackground(): void {
    // Create a simple underwater background
    const gradient = this.add.graphics();
    
    // Fill with gradient
    const width = this.scale.width;
    const height = this.scale.height;
    
    for (let y = 0; y < height; y++) {
      const color = Phaser.Display.Color.Interpolate.ColorWithColor(
        Phaser.Display.Color.ValueToColor(0x1a4e6e),
        Phaser.Display.Color.ValueToColor(0x0d2b3e),
        height,
        y
      );
      
      gradient.fillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b), 1);
      gradient.fillRect(0, y, width, 1);
    }
  }
  
  private createBubbleEffects(): void {
    // Create some decorative bubbles
    for (let i = 0; i < 20; i++) {
      const x = Phaser.Math.Between(0, this.scale.width);
      const y = Phaser.Math.Between(this.scale.height, this.scale.height + 200);
      
      // Create bubble
      if (!this.textures.exists('bubble')) {
        const graphics = this.add.graphics();
        // boubles colors
        const colors = [0x9ad9ea];
        const randomColor = colors[Phaser.Math.Between(0, colors.length - 1)];
        graphics.fillStyle(randomColor, 0.7);
        graphics.fillCircle(8, 8, 8);
        graphics.generateTexture('bubble', 16, 16);
        graphics.destroy();
      }
      
      const bubble = this.add.image(x, y, 'bubble');
      
      // Vary bubble sizes
      const scale = Phaser.Math.FloatBetween(0.5, 1.5);
      bubble.setScale(scale);
      bubble.setAlpha(0.7);
      
      // Animate bubble rising
      this.tweens.add({
        targets: bubble,
        y: -50,
        x: x + Phaser.Math.Between(-100, 100),
        duration: Phaser.Math.Between(3000, 8000),
        ease: 'Linear',
        onComplete: () => {
          // Reset bubble position when it goes off screen
          bubble.y = this.scale.height + 50;
          bubble.x = Phaser.Math.Between(0, this.scale.width);
          
          // Start a new tween
          this.tweens.add({
            targets: bubble,
            y: -50,
            x: bubble.x + Phaser.Math.Between(-100, 100),
            duration: Phaser.Math.Between(3000, 8000),
            ease: 'Linear',
            loop: -1
          });
        }
      });
    }
  }
} 